import { useMemo, useRef, useState } from "react";
import { batchImportUsers, BatchImportUsersResult } from "../../../utils/adminApi";

interface BatchImportUsersModalProps {
  onClose: () => void;
  onSuccess: (count: number) => void;
}

interface ParsedRow {
  line: number; // 源文本中的行号（含表头偏移）
  username: string;
  phone: string;
  err?: string;
}

interface DoneState {
  count: number;
  failCount: number;
  // 已发送的合法行（顺序与服务器返回的 errors.line 对应）
  sentRows: ParsedRow[];
  serverErrors: Array<{ line: number; username: string; reason: string }>;
}

const HEADER_RE = /^(用户|账号|登录名|username|手机|手机号|电话|phone|tel)/i;

// 将手机号规范化：去掉空格/横线（保留前导 +）
function normalizePhone(raw: string): string {
  return raw.replace(/[\s-]/g, "").trim();
}

function isValidPhone(raw: string): boolean {
  return /^\+?\d{5,20}$/.test(normalizePhone(raw));
}

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 解析粘贴文本 / 文件内容。
 * 支持：用户名,手机号（逗号/中文逗号/Tab/空格分隔），首行表头可省略或自动识别。
 */
function parseText(text: string): { rows: ParsedRow[]; hadHeader: boolean; parseError?: string } {
  const cleanText = text.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!cleanText.trim()) return { rows: [], hadHeader: false };

  const rawLines = cleanText.split("\n");
  const rows: ParsedRow[] = [];

  const splitLine = (line: string): string[] => {
    const trimmed = line.trim().replace(/^"|"$/g, "");
    if (!trimmed) return [];
    if (trimmed.includes(",") || trimmed.includes("，") || trimmed.includes("\t")) {
      return trimmed.split(/[,，\t]/).map((t) => t.trim().replace(/^"|"$/g, ""));
    }
    return trimmed.split(/\s+/).map((t) => t.replace(/^"|"$/g, ""));
  };

  let hadHeader = false;
  let startIdx = 0;
  // 表头检测：第一个非空行首列命中表头别名则跳过
  for (let i = 0; i < rawLines.length; i++) {
    const cols = splitLine(rawLines[i]);
    if (cols.length === 0) continue;
    if (HEADER_RE.test(cols[0]) && cols[0].length <= 8) {
      hadHeader = true;
      startIdx = i + 1;
    }
    break;
  }

  const seenUsernames: Record<string, boolean> = {};
  for (let i = startIdx; i < rawLines.length; i++) {
    const cols = splitLine(rawLines[i]);
    if (cols.length === 0 || (cols.length === 1 && !cols[0])) continue;
    if (cols.length < 2) {
      rows.push({ line: i + 1, username: cols[0] || "", phone: "", err: "每行需要“用户名,手机号”两列" });
      continue;
    }
    const username = cols[0];
    const phone = normalizePhone(cols[1] || "");
    let err: string | undefined;
    if (!username) err = "用户名不能为空";
    else if (seenUsernames[username.toLowerCase()]) err = "用户名重复";
    else if (!phone) err = "手机号不能为空";
    else if (!isValidPhone(phone)) err = "手机号格式不正确";
    else seenUsernames[username.toLowerCase()] = true;
    rows.push({ line: i + 1, username, phone, err });
  }

  if (rows.length === 0) {
    return { rows: [], hadHeader, parseError: "未解析到有效数据，请检查格式" };
  }
  return { rows, hadHeader };
}

function buildTemplateText(): string {
  return "用户名,手机号\n张三,13800138000\n李四,13912345678\n王五,15012345678";
}

export default function BatchImportUsersModal({ onClose, onSuccess }: BatchImportUsersModalProps) {
  const now = new Date();
  const past = new Date(now.getTime() - 90 * 24 * 3600 * 1000);
  const [text, setText] = useState("");
  const [regStart, setRegStart] = useState(toLocalInputValue(past));
  const [regEnd, setRegEnd] = useState(toLocalInputValue(now));
  const [password, setPassword] = useState("123456");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState<DoneState | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parsed = useMemo(() => parseText(text), [text]);
  const validRows = parsed.rows.filter((r) => !r.err);
  const errorRows = parsed.rows.filter((r) => r.err);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    const toastEl = document.createElement("div");
    toastEl.style.cssText = `
      position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
      padding: 12px 24px; border-radius: 8px; z-index: 99999;
      background: ${type === "success" ? "#22c55e" : "#ef4444"};
      color: white; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;
    toastEl.textContent = msg;
    document.body.appendChild(toastEl);
    setTimeout(() => toastEl.remove(), 3000);
  };

  const handleFillExample = () => {
    setText(buildTemplateText());
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob(["\uFEFF" + buildTemplateText()], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "用户批量导入模板.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer;
      // 优先 UTF-8；若解码出现乱码替换符或未解析到数据，则尝试 GBK（兼容 Excel 导出的 CSV）
      const decoders = [
        { name: "utf-8", decode: (buf: ArrayBuffer) => new TextDecoder("utf-8").decode(buf) },
        { name: "gbk", decode: (buf: ArrayBuffer) => new TextDecoder("gbk").decode(buf) },
      ];
      let lastError = "";
      for (const dec of decoders) {
        let t: string;
        try {
          t = dec.decode(buffer);
        } catch {
          continue;
        }
        if (t.includes("\uFFFD")) continue; // 解码乱码，尝试下一种编码
        const result = parseText(t);
        if (result.rows.length > 0) {
          setText(t);
          setErrorMsg(null);
          showToast(`已解析 ${result.rows.length} 条数据（${dec.name.toUpperCase()}）`);
          return;
        }
        lastError = result.parseError || lastError;
      }
      setErrorMsg(lastError || "未解析到有效数据，请检查文件格式");
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleSubmit = async () => {
    if (loading) return;
    if (parsed.parseError || parsed.rows.length === 0) {
      setErrorMsg("请先填写要导入的用户数据");
      return;
    }
    if (validRows.length === 0) {
      setErrorMsg("没有可导入的合法数据行，请先修正下方标红的内容");
      return;
    }
    if (regStart > regEnd) {
      setErrorMsg("注册时间开始不能晚于结束");
      return;
    }
    setErrorMsg(null);
    setLoading(true);
    try {
      const result: BatchImportUsersResult = await batchImportUsers({
        users: validRows.map((r) => ({ username: r.username, phone: r.phone })),
        reg_start: regStart.replace("T", " ") || "",
        reg_end: regEnd.replace("T", " ") || "",
        password,
      });
      setDone({
        count: result.count,
        failCount: result.fail_count || 0,
        sentRows: validRows,
        serverErrors: result.errors || [],
      });
      if (result.count > 0) onSuccess(result.count);
    } catch (e: any) {
      setErrorMsg(e?.message || "批量导入失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
              <i className="ri-user-add-line text-orange-500" />
              批量导入用户
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              每行一个用户（用户名,手机号），导入后注册时间将在下方所选范围内随机生成
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        {done ? (
          /* ---------- 结果页 ---------- */
          <div className="p-6 overflow-auto">
            <div className="flex flex-col items-center py-6">
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  done.count > 0 ? "bg-green-100" : "bg-red-100"
                }`}
              >
                <i
                  className={`${
                    done.count > 0 ? "ri-checkbox-circle-line text-green-500" : "ri-close-circle-line text-red-500"
                  } text-3xl`}
                />
              </div>
              <p className="text-lg font-bold text-gray-800 mt-4">导入完成</p>
              <p className="text-sm text-gray-500 mt-1">
                成功导入 <strong className="text-green-600">{done.count}</strong> 个用户
                {done.failCount > 0 && (
                  <>
                    ，失败 <strong className="text-red-500">{done.failCount}</strong> 个
                  </>
                )}
              </p>
            </div>

            {(done.serverErrors.length > 0 || errorRows.length > 0) && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 mt-2">
                <div className="text-sm font-medium text-red-600 mb-2 flex items-center gap-1.5">
                  <i className="ri-error-warning-line" />
                  以下行未能导入
                </div>
                <div className="text-xs text-red-500 space-y-1 max-h-40 overflow-auto">
                  {[
                    ...errorRows.map((r) => ({ line: r.line, username: r.username, reason: r.err || "" })),
                    ...done.serverErrors.map((e) => {
                      const row = done.sentRows[e.line - 1];
                      return { line: row ? row.line : e.line, username: e.username, reason: e.reason };
                    }),
                  ].map((e, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="shrink-0 text-gray-400">第 {e.line} 行</span>
                      <span className="font-medium shrink-0">{e.username || "-"}</span>
                      <span className="text-red-400">{e.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                关闭
              </button>
              <button
                onClick={() => {
                  setDone(null);
                  setText("");
                }}
                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer"
              >
                继续导入
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-50 bg-gray-50/50 shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleFillExample}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 rounded-lg cursor-pointer transition-colors"
                >
                  <i className="ri-file-text-line" />
                  填入示例
                </button>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-500 rounded-lg cursor-pointer transition-colors"
                >
                  <i className="ri-download-line" />
                  下载模板
                </button>
                <label className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-600 hover:border-green-400 hover:text-green-500 rounded-lg cursor-pointer transition-colors">
                  <i className="ri-upload-line" />
                  上传CSV/文本
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span>
                  共 <strong className="text-gray-600">{parsed.rows.length}</strong> 行
                </span>
                <span>
                  有效 <strong className="text-orange-500">{validRows.length}</strong> 条
                </span>
                {errorRows.length > 0 && (
                  <span>
                    错误 <strong className="text-red-500">{errorRows.length}</strong> 行
                  </span>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-gray-600 font-medium">用户数据（每行：用户名,手机号）</label>
                  <span className="text-xs text-gray-400">手机号可含空格/横线，支持中文逗号或 Tab 分隔</span>
                </div>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={8}
                  placeholder={"示例：\n张三,13800138000\n李四,13912345678"}
                  className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-xl focus:outline-none focus:border-orange-400 bg-gray-50/50 leading-6"
                />
              </div>

              {/* 解析预览 */}
              {parsed.rows.length > 0 && (
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 text-xs text-gray-400 border-b border-gray-100">
                    解析预览
                  </div>
                  <div className="max-h-44 overflow-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-white text-xs text-gray-400 border-b border-gray-100">
                          <th className="text-left px-4 py-2 font-medium w-16">行号</th>
                          <th className="text-left px-4 py-2 font-medium w-40">用户名</th>
                          <th className="text-left px-4 py-2 font-medium w-44">手机号</th>
                          <th className="text-left px-4 py-2 font-medium">状态</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {parsed.rows.map((r, idx) => (
                          <tr key={idx} className={r.err ? "bg-red-50/60" : ""}>
                            <td className="px-4 py-1.5 text-xs text-gray-400">#{r.line}</td>
                            <td className="px-4 py-1.5 text-gray-700">{r.username || "-"}</td>
                            <td className="px-4 py-1.5 text-gray-600">{r.phone || "-"}</td>
                            <td className="px-4 py-1.5">
                              {r.err ? (
                                <span className="text-xs text-red-500">{r.err}</span>
                              ) : (
                                <span className="text-xs text-green-600 flex items-center gap-1">
                                  <i className="ri-checkbox-circle-line" />
                                  待导入
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 注册时间范围 */}
              <div className="bg-orange-50/70 border border-orange-100 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-3">
                  <i className="ri-time-line text-orange-500 text-sm" />
                  <span className="text-sm font-medium text-gray-700">注册时间随机范围</span>
                  <span className="text-xs text-gray-400 ml-1">每个导入用户的注册时间将在该范围内随机生成</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="datetime-local"
                    value={regStart}
                    onChange={(e) => setRegStart(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-orange-400"
                  />
                  <span className="text-gray-400 text-sm">至</span>
                  <input
                    type="datetime-local"
                    value={regEnd}
                    onChange={(e) => setRegEnd(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-orange-400"
                  />
                </div>
              </div>

              {/* 默认密码 */}
              <div className="flex items-center gap-3">
                <label className="text-xs text-gray-600 font-medium shrink-0 w-28">默认登录密码</label>
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="导入用户的统一初始密码（至少6位）"
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                />
                <span className="text-xs text-gray-400">导入后可在“编辑”中单独修改</span>
              </div>

              {errorMsg && (
                <div className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-center gap-1.5">
                  <i className="ri-error-warning-line" />
                  {errorMsg}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
              <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                取消
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || validRows.length === 0}
                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    导入中...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-2-line" />
                    开始导入{validRows.length > 0 ? `（${validRows.length}条）` : ""}
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
